import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import isFunction from 'lodash/isFunction';
import Tooltip from '@Components/Tooltip';
import '../RsSlidable/RsSlidable.custom.scss';
import min from 'lodash/min';
import max from 'lodash/max';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { connect } from 'react-redux';
import { loadItems, clearState } from '@Pages/RetroSynthesis/components/RsSlidablePaginated/actions';
import { REDUCER_KEY_RS } from '@Pages/RetroSynthesis/RetroSynthesisProjects/constants';
import { REDUCER_KEY_RS_SLIDABLE_PAGINATED } from '@Pages/RetroSynthesis/components/RsSlidablePaginated/constants';
import RsSlidablePaginatedNavigationWithDynamicItem from '@Pages/RetroSynthesis/components/RsSlidablePaginated/RsSlidablePaginatedNavigationWithDynamicItem';
import debounce from 'lodash/debounce';

/* eslint-disable no-shadow */
/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
export function RsSlidablePaginated({
  className,
  containerClassName,
  contentClassName,
  itemClassName,
  loadItemsAsync,
  renderItem,
  selectedItem,
  centerItemsIfNavigationControlsNotVisible,
  getSelectedItemIndex,
  minimumItemWidth,
  defaultPageSize,
  items,
  totalElements,
  totalPages,
  loadedPages,
  partialLastPageLoaded,
  loadItems,
  reloadToItem,
  clearState,
}) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const itemsRef = useRef([]);

  const [maxPageSize, setMaxPageSize] = useState(defaultPageSize);
  const [visibleItemIndex, setVisibleItemIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isOverflown, setIsOverflown] = useState(false);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [contentOffset, setContentOffset] = useState(0);

  const preloadItems = (page) => {
    if (!maxPageSize) {
      return;
    }
    const params = { size: maxPageSize };
    if (!totalElements) {
      // initial load page with selected item
      params.selectedItem = reloadToItem || selectedItem;
    } else {
      // normal next page loading
      params.page = page;
    }
    setIsLoading(true);
    loadItems(params, loadItemsAsync);
  };

  const updateIsOverflown = () => {
    const containerWidth = containerRef.current?.offsetWidth;
    const contentWidth = contentRef.current?.offsetWidth;
    setIsOverflown(contentWidth > containerWidth || totalPages > 1);
  };

  const scrollTo = debounce((offset) => {
    containerRef.current?.scrollTo({
      left: offset,
      behavior: 'smooth',
    });
  }, 100);

  const updateContentOffset = () => {
    if (!isOverflown) {
      setContentOffset(0);
      scrollTo(0);
    }
  };

  const isNoItemsAtRight = (withHalfWidth = false) => {
    if (itemsRef.current?.length) {
      const lastItem = itemsRef?.current[itemsRef.current.length - 1];
      return (
        lastItem?.getBoundingClientRect() &&
        lastItem?.getBoundingClientRect().right <
          containerRef.current?.getBoundingClientRect()?.right + (withHalfWidth ? 0 : minimumItemWidth)
      );
    }
    return false;
  };

  const isNoItemsAtLeft = (withHalfWidth = false) => {
    if (itemsRef.current?.length) {
      const firstItem = itemsRef?.current[0];
      return (
        firstItem?.getBoundingClientRect() &&
        firstItem?.getBoundingClientRect().left >=
          containerRef.current?.getBoundingClientRect()?.left - (withHalfWidth ? minimumItemWidth / 2 : 0)
      );
    }
    return false;
  };

  const updateDisabledNavigation = () => {
    const minPage = min(loadedPages);
    const maxPage = max(loadedPages);
    setPrevDisabled(isNoItemsAtLeft(true) && minPage === 0);
    setNextDisabled(isNoItemsAtRight(true) && maxPage >= totalPages - 1);
  };

  // Restrict left and right edge cases and call setOffset and/or load next page
  const applyContentOffset = (offset) => {
    if (offset <= 8 && isNoItemsAtLeft()) {
      setContentOffset(0);
      scrollTo(0);
    } else {
      setContentOffset(offset);
      scrollTo(offset);
    }
  };

  const debouncedScrollHandler = debounce(() => {
    const leftPadding = containerRef.current?.scrollLeft;
    const contentWidth = contentRef.current?.offsetWidth;
    const containerWidth = containerRef.current?.offsetWidth;
    const rightPadding = contentWidth - leftPadding - containerWidth;
    if (!rightPadding) {
      const maxPage = max(loadedPages);
      if (items.length && items.length < totalElements && maxPage < totalPages - 1 && !partialLastPageLoaded) {
        preloadItems(maxPage + 1);
      }
    }
    if (!leftPadding) {
      const minPage = min(loadedPages);
      if (items.length && items.length < totalElements && minPage > 0) {
        preloadItems(minPage - 1);
      }
    }
    updateDisabledNavigation();
  }, 350);

  const calculatePageSize = () => {
    if (defaultPageSize) {
      // if page size predefined skip
      return;
    }
    const containerWidth = containerRef.current?.offsetWidth;
    const newMaxPageSize = Math.ceil(containerWidth / minimumItemWidth);
    if (newMaxPageSize !== maxPageSize) {
      setMaxPageSize(newMaxPageSize);
    } else {
      preloadItems();
    }
  };

  useEffect(() => {
    return clearState; // Clean redux state in case component unmounted
  }, []);

  // initial load
  useEffect(() => {
    if (totalElements === 0) {
      setVisibleItemIndex(-1);
      calculatePageSize();
    }
  }, [totalElements]);

  // Remove excess itemsRef when items length change
  useEffect(() => {
    if (!items || !items.length) {
      return;
    }
    itemsRef.current = itemsRef.current?.slice(0, items.length);
    updateIsOverflown();

    // rebuild tooltip if it is used for new items
    Tooltip.rebuild();
    setTimeout(() => {
      // pause until items completely rendered to get correct width
      if ((partialLastPageLoaded && loadedPages.length === 2) || (!partialLastPageLoaded && loadedPages.length === 1)) {
        // scroll to selectedItem on first load
        let index = getSelectedItemIndex(items, selectedItem);
        // if index of new item the same as for previous scroll to one more left
        index = index === visibleItemIndex && index > 0 ? index - 1 : index;
        setVisibleItemIndex(index);
      }
      updateDisabledNavigation();
      setIsLoading(false);
    }, 300);
  }, [items]);

  // updateDisabledNavigation on contentOffset prop change
  useEffect(() => {
    const timer = setTimeout(() => {
      updateDisabledNavigation();
    }, 350);
    return () => clearTimeout(timer);
  }, [contentOffset]);

  // reset offset if items fit container
  useEffect(updateContentOffset, [isOverflown]);

  // initial items loading
  useEffect(() => {
    preloadItems();
  }, [maxPageSize]);

  // reload all from action
  useEffect(() => {
    if (reloadToItem) {
      preloadItems();
    }
  }, [reloadToItem]);

  return (
    <div className={cn('rs-slidable', 'rs-slidable-paginated', className)}>
      <div
        ref={containerRef}
        onScroll={debouncedScrollHandler}
        className={cn(
          'rs-slidable__container',
          'rs-slidable-paginated__container',
          { 'rs-slidable__container-centered': !isOverflown && centerItemsIfNavigationControlsNotVisible },
          containerClassName
        )}>
        {isLoading && <SimpleSpinner />}
        <ul
          ref={contentRef}
          className={cn('rs-slidable__content', 'rs-slidable-paginated__content', contentClassName)}
          // style={rightContentOffset ? { left: `auto`, right: `${rightContentOffset}px` } : { left: `${contentOffset}px` }}>
        >
          {items &&
            items.length > 0 &&
            items.map((item, i) => {
              const itemCN = isFunction(itemClassName) ? itemClassName(item, i) : itemClassName;
              return (
                // eslint-disable-next-line react/no-array-index-key,no-return-assign
                <li key={i} ref={(el) => (itemsRef.current[i] = el)} className={cn('rs-slidable__content-item', itemCN)}>
                  {renderItem(item, i)}
                </li>
              );
            })}
        </ul>
      </div>
      {isOverflown && (
        <RsSlidablePaginatedNavigationWithDynamicItem
          containerElement={containerRef.current}
          contentElement={contentRef.current}
          contentOffset={contentOffset}
          applyContentOffset={applyContentOffset}
          prevDisabled={prevDisabled}
          nextDisabled={nextDisabled}
          itemsElements={itemsRef.current}
          visibleItemIndex={visibleItemIndex}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

RsSlidablePaginated.defaultProps = {
  renderItem: () => {},
  centerItemsIfNavigationControlsNotVisible: false,
  defaultPageSize: 0,
};

RsSlidablePaginated.propTypes = {
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  itemClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  loadItemsAsync: PropTypes.func.isRequired,
  renderItem: PropTypes.func,
  selectedItem: PropTypes.string.isRequired,
  getSelectedItemIndex: PropTypes.func.isRequired,
  minimumItemWidth: PropTypes.number.isRequired,
  defaultPageSize: PropTypes.number,
  centerItemsIfNavigationControlsNotVisible: PropTypes.bool,
};

function mapStateToProps(state) {
  const rsState = state && state.get && state.get(REDUCER_KEY_RS);
  const { items, totalElements, totalPages, loadedPages, partialLastPageLoaded, reloadToItem } =
    rsState.get(REDUCER_KEY_RS_SLIDABLE_PAGINATED);
  return {
    items,
    totalElements,
    totalPages,
    loadedPages,
    partialLastPageLoaded,
    reloadToItem,
  };
}

const mapDispatchToProps = {
  loadItems,
  clearState,
};

export default connect(mapStateToProps, mapDispatchToProps)(RsSlidablePaginated);
